<script setup>
import Layout from "../Layouts/Customer.vue";
import { Link } from "@inertiajs/inertia-vue3";
import { computed, ref } from "vue";
import Card from "primevue/card";
import Button from "primevue/button";
import Tag from "primevue/tag";
import ProgressSpinner from "primevue/progressspinner";
import Message from 'primevue/message';

const props = defineProps([
    'customer',
    'menuItems',
    'device',
    'bookings',
    'pagination',
    'currentPage',
    'error'
]);

// Loading state
const loading = ref(false);

// Format date and time in German format
const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Get status color
const getStatusColor = (booking) => {
    if (booking.status === 'confirmed') return 'info';
    return 'warning';
};

// Get status label
const getStatusLabel = (booking) => {
    if (booking.status === 'confirmed') return 'Bestätigt';
    return booking.status;
};

// Pagination helpers
const totalPages = computed(() => props.pagination?.pages_count || 0);
const hasNextPage = computed(() => props.currentPage < totalPages.value);
const hasPrevPage = computed(() => props.currentPage > 1);

// Generate page numbers to display
const pageNumbers = computed(() => {
    const current = props.currentPage;
    const total = totalPages.value;
    const pages = [];
    
    if (total <= 7) {
        // Show all pages if 7 or less
        for (let i = 1; i <= total; i++) {
            pages.push(i);
        }
    } else {
        // Always show first page
        pages.push(1);
        
        if (current > 3) {
            pages.push('...');
        }
        
        // Show pages around current
        for (let i = Math.max(2, current - 1); i <= Math.min(current + 1, total - 1); i++) {
            pages.push(i);
        }
        
        if (current < total - 2) {
            pages.push('...');
        }
        
        // Always show last page
        pages.push(total);
    }
    
    return pages;
});

// Navigate to page
const goToPage = (page) => {
    if (page === '...' || page === props.currentPage) return;
    loading.value = true;
    window.location.href = `/customers/${props.customer.uuid}/simplybook-bookings/${props.device.uuid}?page=${page}`;
};

// Download PDF
const downloadPDF = () => {
    const page = props.currentPage || 1;
    window.location.href = `/customers/${props.customer.uuid}/simplybook-bookings/${props.device.uuid}/export?page=${page}`;
};
</script>

<template>
    <Layout :customer="customer" :menuItems="menuItems">
        <div class="container mx-auto px-4 py-6">
            <!-- Header -->
            <div class="mb-6 relative">
                <div class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer text-xl" @click="downloadPDF"></i>
                </div>
                <h1 class="text-2xl font-bold mb-2">SimplyBook Buchungen</h1>
                <div class="flex items-center gap-4 text-gray-600">
                    <span>Kunde: <strong>{{ customer.forename }} {{ customer.surname }}</strong></span>
                    <span>Gerät: <strong>{{ device.name }}</strong></span>
                </div>
            </div>

            <!-- Error Message -->
            <Message v-if="error" severity="error" :closable="false" class="mb-4">
                {{ error }}
            </Message>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center items-center py-12">
                <ProgressSpinner />
            </div>

            <!-- Empty State -->
            <div v-else-if="!bookings || bookings.length === 0" class="text-center py-12">
                <i class="pi pi-calendar text-6xl text-gray-300 mb-4"></i>
                <p class="text-xl text-gray-600">Keine Buchungen gefunden</p>
                <p class="text-gray-500 mt-2">Es gibt keine Buchungen für dieses Gerät.</p>
            </div>

            <!-- Bookings Grid -->
            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card v-for="booking in bookings" :key="booking.id" class="shadow-sm">
                    <template #header>
                        <div class="flex justify-between items-center p-4 pb-0">
                            <Tag :severity="getStatusColor(booking)" :value="getStatusLabel(booking)" />
                            <span class="text-sm text-gray-500">{{ booking.code || booking.id }}</span>
                        </div>
                    </template>
                    
                    <template #content>
                        <div class="space-y-2">
                            <!-- Date and Time -->
                            <div class="flex items-center gap-2">
                                <i class="pi pi-calendar text-gray-400"></i>
                                <span class="font-semibold">{{ formatDateTime(booking.start_datetime) }}</span>
                            </div>
                            
                            <!-- Duration -->
                            <div class="flex items-center gap-2" v-if="booking.end_datetime">
                                <i class="pi pi-clock text-gray-400"></i>
                                <span>bis {{ formatDateTime(booking.end_datetime) }}</span>
                            </div>
                            
                            <!-- Service -->
                            <div class="flex items-center gap-2" v-if="booking.service">
                                <i class="pi pi-tag text-gray-400"></i>
                                <span>{{ booking.service.name }}</span>
                            </div>
                            
                            <!-- Provider -->
                            <div class="flex items-center gap-2" v-if="booking.provider">
                                <i class="pi pi-user text-gray-400"></i>
                                <span>{{ booking.provider.name }}</span>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-8">
                <!-- First Page -->
                <Button
                    label="Erste"
                    :disabled="!hasPrevPage"
                    @click="goToPage(1)"
                    text
                    size="small"
                    class="px-3"
                />
                
                <!-- Previous Page -->
                <Button
                    icon="pi pi-chevron-left"
                    :disabled="!hasPrevPage"
                    @click="goToPage(currentPage - 1)"
                    text
                    size="small"
                />
                
                <!-- Page Numbers -->
                <template v-for="page in pageNumbers" :key="page">
                    <span v-if="page === '...'" class="px-2 text-gray-400">...</span>
                    <Button
                        v-else
                        :label="String(page)"
                        :outlined="page !== currentPage"
                        :severity="page === currentPage ? 'primary' : 'secondary'"
                        @click="goToPage(page)"
                        size="small"
                        class="min-w-[2.5rem]"
                    />
                </template>
                
                <!-- Next Page -->
                <Button
                    icon="pi pi-chevron-right"
                    :disabled="!hasNextPage"
                    @click="goToPage(currentPage + 1)"
                    text
                    size="small"
                />
                
                <!-- Last Page -->
                <Button
                    label="Letzte"
                    :disabled="!hasNextPage"
                    @click="goToPage(totalPages)"
                    text
                    size="small"
                    class="px-3"
                />
            </div>

            <!-- Pagination Info -->
            <div v-if="pagination" class="text-center text-sm text-gray-500 mt-4">
                Zeige {{ bookings.length }} von {{ pagination.items_count }} Buchungen
                (Seite {{ currentPage }} von {{ totalPages }})
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.p-card {
    transition: transform 0.2s, box-shadow 0.2s;
}

.p-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>